import { PATHS } from "../../const/paths";

const STORAGE_KEY = 'pending_share_token';
const SENTINEL = '_pending';
const SHARE_PATH_PREFIX = `/${PATHS.SHARE_APERCU}/`;

/**
 * Sentinel inserted in the URL path in place of the actual share token so the
 * latter never appears in the Keycloak redirect_uri (and therefore is kept out
 * of Keycloak access/audit logs and federated-IdP referer chains).
 */
export const SHARE_TOKEN_SENTINEL = SENTINEL;

/**
 * If the given URL points to a /share/:token route, persist the token in
 * sessionStorage and return a scrubbed URL where the token segment is replaced
 * by SHARE_TOKEN_SENTINEL. Otherwise return the original URL unchanged.
 */
export function stashShareTokenAndScrubUrl(href: string): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }

  const path = url.pathname;
  const idx = path.indexOf(SHARE_PATH_PREFIX);
  if (idx === -1) return href;

  const tail = path.substring(idx + SHARE_PATH_PREFIX.length);
  const token = tail.split('/')[0];
  if (!token || token === SENTINEL) return href;

  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // sessionStorage unavailable (private mode, quota): fall back to the
    // original URL — the token still flows through Keycloak but the app keeps
    // working rather than losing the link.
    return href;
  }

  url.pathname = path.substring(0, idx) + SHARE_PATH_PREFIX + SENTINEL + path.substring(idx + SHARE_PATH_PREFIX.length + token.length);
  return url.toString();
}

/**
 * Read and remove the stashed share token. Returns null if no token is stashed
 * (direct navigation to /share/_pending, expired tab, sessionStorage disabled).
 */
export function consumeStashedShareToken(): string | null {
  let token: string | null = null;
  try {
    token = sessionStorage.getItem(STORAGE_KEY);
    if (token) sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return null;
  }
  return token;
}
